

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Edit3,
  Loader2,
  AlertCircle,
  RefreshCw,
  BookOpen,
  Layers3,
  CircleCheck,
  CircleX,
  FileText,
  Award,
  Clock3,
  Info,
} from "lucide-react";

import {
  useGetEnglishQuestionByIdQuery,
} from "../../slices/englishQuestionApiSlice";

import QuestionStatusBadge from "./components/QuestionStatusBadge";

const QUESTION_TYPE_LABELS = {
  singleChoice: "Single Choice",
  multipleChoice: "Multiple Choice",
  statementBased: "Statement Based",
  synonym: "Synonym",
  antonym: "Antonym",
  vocabulary: "Vocabulary",
  fillInTheBlank: "Fill in the Blank",
  errorDetection: "Error Detection",
  sentenceImprovement: "Sentence Improvement",
  phraseReplacement: "Phrase Replacement",
  clozeTest: "Cloze Test",
  paraJumble: "Para Jumble",
  matchFollowing: "Match the Following",
};

const DIFFICULTY_STYLES = {
  easy: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  medium: "bg-amber-50 text-amber-700 ring-amber-600/20",
  hard: "bg-red-50 text-red-700 ring-red-600/20",
};

const getErrorMessage = (error) =>
  error?.data?.message ||
  error?.message ||
  error?.error ||
  "Unable to load the English question.";

const getId = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value._id || value.id || null;
  }

  return value;
};

const getName = (value, fallback = "Not assigned") => {
  if (!value) return fallback;

  if (typeof value === "object") {
    return value.name || value.title || value._id || fallback;
  }

  return value;
};

const formatDate = (date) => {
  if (!date) return "—";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return parsedDate.toLocaleString();
};

const getOptionLabel = (index) => {
  return String.fromCharCode(65 + index);
};

const EnglishQuestionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetEnglishQuestionByIdQuery(id, {
    skip: !id,
  });

  // Supports both:
  // { question: {...} }
  // and direct question response: {...}
  const question =
    response?.question ||
    response?.data ||
    response;

  const handleBack = () => {
    navigate("/english-questions");
  };

  const handleEdit = () => {
    navigate(`/english-questions/${id}/edit`);
  };

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto flex min-h-[500px] max-w-7xl items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-9 w-9 animate-spin text-indigo-600" />

            <p className="text-sm font-medium text-slate-500">
              Loading English question...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (isError || !question) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to English Questions
            </button>
          </div>
        </div>

        <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-red-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>

              <div className="flex-1">
                <h2 className="font-semibold text-slate-900">
                  Unable to load English question
                </h2>

                <p className="mt-1 text-sm text-slate-600">
                  {getErrorMessage(error)}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>

                  <button
                    type="button"
                    onClick={handleBack}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const questionType =
    QUESTION_TYPE_LABELS[question.questionType] ||
    question.questionType ||
    "Not specified";

  const difficulty =
    question.difficulty || "Not specified";

  const options = Array.isArray(question.options)
    ? question.options
    : [];

  const statements = Array.isArray(question.statements)
    ? question.statements
    : [];

  const correctAnswer = question.correctAnswer;

  const group = question.group;
  const chapter = question.chapter;
  const subject = question.subject;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* =====================================================
          Header
      ====================================================== */}

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Left */}
            <div className="flex min-w-0 items-start gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                aria-label="Back to English questions"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
                    English Question Details
                  </h1>

                  {question.isActive !== undefined && (
                    <QuestionStatusBadge
                      isActive={question.isActive}
                    />
                  )}

                  {isFetching && (
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                  )}
                </div>

                <p className="mt-1 text-sm text-slate-500">
                  View the complete English question and its configuration.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Back
              </button>

              <button
                type="button"
                onClick={handleEdit}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
              >
                <Edit3 className="h-4 w-4" />
                Edit Question
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          Main
      ====================================================== */}

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* =================================================
              Main Question
          ================================================== */}

          <div className="space-y-6 xl:col-span-2">
            {/* Question Content */}
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
                    <FileText className="h-4 w-4 text-indigo-600" />
                  </div>

                  <div>
                    <h2 className="font-semibold text-slate-900">
                      Question
                    </h2>

                    <p className="text-xs text-slate-500">
                      Question content and highlighted text
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6">
                <div className="rounded-lg bg-slate-50 p-4 sm:p-5">
                  <p className="whitespace-pre-wrap text-base leading-7 text-slate-900">
                    {question.questionText || "No question text available."}
                  </p>
                </div>

                {question.highlightedText && (
                  <div className="mt-5">
                    <h3 className="mb-2 text-sm font-semibold text-slate-700">
                      Highlighted Text
                    </h3>

                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <p className="whitespace-pre-wrap text-sm leading-6 text-amber-900">
                        {question.highlightedText}
                      </p>
                    </div>
                  </div>
                )}

                {question.imageUrl && (
                  <div className="mt-5">
                    <h3 className="mb-2 text-sm font-semibold text-slate-700">
                      Question Image
                    </h3>

                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                      <img
                        src={question.imageUrl}
                        alt="Question"
                        className="max-h-[500px] w-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Statements */}
            {statements.length > 0 && (
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
                  <h2 className="font-semibold text-slate-900">
                    Statements
                  </h2>

                  <p className="mt-1 text-xs text-slate-500">
                    Statements associated with this question
                  </p>
                </div>

                <div className="space-y-3 p-5 sm:p-6">
                  {statements.map((statement, index) => {
                    const statementText =
                      typeof statement === "object"
                        ? statement.text ||
                          statement.statement ||
                          ""
                        : statement;

                    return (
                      <div
                        key={statement._id || index}
                        className="flex gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                          {index + 1}
                        </span>

                        <p className="whitespace-pre-wrap pt-0.5 text-sm leading-6 text-slate-700">
                          {statementText || "Empty statement"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Options */}
            {options.length > 0 && (
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
                  <h2 className="font-semibold text-slate-900">
                    Answer Options
                  </h2>

                  <p className="mt-1 text-xs text-slate-500">
                    Available choices and the correct answer
                  </p>
                </div>

                <div className="space-y-3 p-5 sm:p-6">
                  {options.map((option, index) => {
                    const optionValue =
                      typeof option === "object"
                        ? option.value ??
                          option.text ??
                          option.label ??
                          ""
                        : option;

                    const optionId =
                      typeof option === "object"
                        ? option._id
                        : null;

                    const normalizedCorrect =
                      Array.isArray(correctAnswer)
                        ? correctAnswer.map(String)
                        : [String(correctAnswer ?? "")];

                    const isCorrect =
                      normalizedCorrect.includes(
                        String(optionValue),
                      ) ||
                      (optionId &&
                        normalizedCorrect.includes(
                          String(optionId),
                        )) ||
                      (typeof option === "object" &&
                        option.isCorrect === true);

                    return (
                      <div
                        key={optionId || index}
                        className={`flex items-start gap-3 rounded-lg border p-4 ${
                          isCorrect
                            ? "border-emerald-300 bg-emerald-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                            isCorrect
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {getOptionLabel(index)}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p
                            className={`whitespace-pre-wrap text-sm leading-6 ${
                              isCorrect
                                ? "font-medium text-emerald-900"
                                : "text-slate-700"
                            }`}
                          >
                            {String(optionValue)}
                          </p>
                        </div>

                        {isCorrect && (
                          <CircleCheck className="h-5 w-5 shrink-0 text-emerald-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Correct Answer */}
            {correctAnswer !== undefined &&
              correctAnswer !== null && (
                <section className="overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
                  <div className="border-b border-emerald-100 bg-emerald-50 px-5 py-4 sm:px-6">
                    <div className="flex items-center gap-2">
                      <CircleCheck className="h-5 w-5 text-emerald-600" />

                      <h2 className="font-semibold text-emerald-900">
                        Correct Answer
                      </h2>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                      <p className="whitespace-pre-wrap text-sm font-medium text-emerald-900">
                        {Array.isArray(correctAnswer)
                          ? correctAnswer.join(", ")
                          : String(correctAnswer)}
                      </p>
                    </div>
                  </div>
                </section>
              )}

            {/* Explanation */}
            {question.explanation && (
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
                  <h2 className="font-semibold text-slate-900">
                    Explanation
                  </h2>

                  <p className="mt-1 text-xs text-slate-500">
                    Explanation shown after answering the question
                  </p>
                </div>

                <div className="p-5 sm:p-6">
                  <div className="rounded-lg bg-blue-50 p-4">
                    <p className="whitespace-pre-wrap text-sm leading-7 text-blue-950">
                      {question.explanation}
                    </p>
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* =================================================
              Sidebar
          ================================================== */}

          <div className="space-y-6">
            {/* Question Configuration */}
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50">
                    <Info className="h-4 w-4 text-violet-600" />
                  </div>

                  <div>
                    <h2 className="font-semibold text-slate-900">
                      Configuration
                    </h2>

                    <p className="text-xs text-slate-500">
                      Question settings
                    </p>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                <InfoRow
                  label="Question Type"
                  value={questionType}
                />

                <InfoRow
                  label="Difficulty"
                  value={
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset ${
                        DIFFICULTY_STYLES[question.difficulty] ||
                        "bg-slate-100 text-slate-600 ring-slate-500/20"
                      }`}
                    >
                      {difficulty}
                    </span>
                  }
                />

                <InfoRow
                  label="Marks"
                  value={question.marks ?? 0}
                  icon={<Award className="h-4 w-4" />}
                />

                <InfoRow
                  label="Negative Marks"
                  value={question.negativeMarks ?? 0}
                  icon={<CircleX className="h-4 w-4" />}
                />

                <InfoRow
                  label="Order"
                  value={question.order ?? "—"}
                />
              </div>
            </section>

            {/* Classification */}
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
                    <BookOpen className="h-4 w-4 text-emerald-600" />
                  </div>

                  <div>
                    <h2 className="font-semibold text-slate-900">
                      Classification
                    </h2>

                    <p className="text-xs text-slate-500">
                      Question bank organization
                    </p>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                <InfoRow
                  label="Subject"
                  value={getName(subject)}
                />

                <InfoRow
                  label="Chapter"
                  value={getName(chapter)}
                />

                <InfoRow
                  label="Question Group"
                  value={getName(group)}
                  icon={<Layers3 className="h-4 w-4" />}
                />
              </div>
            </section>

            {/* Record Information */}
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                    <Clock3 className="h-4 w-4 text-slate-600" />
                  </div>

                  <div>
                    <h2 className="font-semibold text-slate-900">
                      Record Information
                    </h2>

                    <p className="text-xs text-slate-500">
                      Question metadata
                    </p>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                <InfoRow
                  label="Question ID"
                  value={
                    question._id || id || "—"
                  }
                  mono
                />

                <InfoRow
                  label="Created"
                  value={formatDate(question.createdAt)}
                />

                <InfoRow
                  label="Last Updated"
                  value={formatDate(question.updatedAt)}
                />
              </div>
            </section>

            {/* Status */}
            {question.isActive !== undefined && (
              <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Question Status
                    </h3>

                    <p className="mt-1 text-xs text-slate-500">
                      Current availability in the question bank.
                    </p>
                  </div>

                  <QuestionStatusBadge
                    isActive={question.isActive}
                  />
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// =====================================================
// Reusable Information Row
// =====================================================

const InfoRow = ({
  label,
  value,
  icon,
  mono = false,
}) => {
  return (
    <div className="px-5 py-3.5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
          {icon && (
            <span className="text-slate-400">
              {icon}
            </span>
          )}

          <span>{label}</span>
        </div>

        <div
          className={`max-w-[60%] text-right text-sm font-medium text-slate-900 ${
            mono ? "break-all font-mono text-xs" : ""
          }`}
        >
          {value ?? "—"}
        </div>
      </div>
    </div>
  );
};

export default EnglishQuestionDetails;

